<template>
  <div class="home" v-if="page">
    <loading :active.sync="isLoading" :is-full-page="true" color="#4e97b2"></loading>
    <section aria-label="Promote your organization online">
      <img class="header-image" :src="page.fields.bannerid" :alt="page.fields.image_alt_text" />
      <div class="container">
        <div class="row">
          <div class="col-lg-6 mt-5 text-left">
            <b-card>
              <form @submit.prevent="signIn">
                <b-form-group label="Email" label-for="email">
                  <b-form-input id="email" v-model="lowercaseEmail" placeholder="Email" type="text" :state="submitted && $v.email.$error ? false : null" />
                  <b-form-invalid-feedback v-if="submitted && !$v.email.required">Email is required</b-form-invalid-feedback>
                  <b-form-invalid-feedback v-if="submitted && !$v.email.email">Email needs to be a valid email</b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Password" label-for="password">
                  <b-form-input
                    id="password"
                    name="password"
                    v-model="password"
                    placeholder="Password"
                    type="password"
                    :state="submitted && $v.password.$error ? false : null"
                  />
                  <b-form-invalid-feedback v-if="submitted && !$v.password.required">Password is required</b-form-invalid-feedback>
                </b-form-group>
                <span v-if="errorMsg !== null" class="error-message mb-2">
                  {{ errorMsg }}
                </span>
                <b-button type="submit" :disabled="signInButtonDisabled && submitted && $v.$invalid">Sign In</b-button>
                <router-link to="/auth/forgotpassword" class="ml-3 btn btn-white">Forgot Password</router-link>
              </form>
            </b-card>
            <h1 v-html="page.fields.title"></h1>
            <p v-html="page.fields.description_title"></p>
            <p v-html="page.fields.description"></p>
          </div>
          <div class="col-lg-6">
            <div
              role="presentation"
              alt="Example page for alabama.travel on desktop and tablet"
              class="callout-img-right-1"
              :style="{ backgroundImage: `url('${page.fields.callout_1_image}')` }"
            ></div>
          </div>
        </div>
      </div>
    </section>
    <section class="footer-control">
      <footer>
        <div class="container">
          <div class="row">
            <div class="col-lg-8 text-left">
              <div class="row float-none">
                <div>
                  <ul class="social-links-container">
                    <li v-for="item in page.fields.social" :key="item.url">
                      <a :href="item.url" target="_blank" rel="noopener noreferrer" class="social-footer-link">
                        <div class="footer-social-img" :style="{ backgroundImage: `url('${item.image}')` }" alt="{item.alt}" />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              <div class="row float-none mt-4 footer-block">
                <h6>Sweet Home Alabama</h6>
                <div class="tourism-links">
                  ©2001-{{ new Date().getFullYear() }} Alabama Tourism Department |
                  <a
                    href=" https://alabama.travel/privacy-policy"
                    rel="external noopener"
                    target="_blank"
                    class="privacy-policy"
                    v-html="page.fields.privacy_policy"
                  ></a>
                </div>
              </div>

              <div class="row float-none mt-2 footer-block">
                <aside aria-label="Other Alabama Tourism websites">
                  <h6 v-html="page.fields.tourism_websites"></h6>
                  <div class="tourism-links" v-html="page.fields.travel_link"></div>
                  <div class="tourism-links" v-html="page.fields.partners_link"></div>
                </aside>
              </div>
            </div>
            <div class="col-lg-4">
              <aside aria-label="Other sites of interest">
                <a href="https://www.visittheusa.com/state/alabama" rel="external noopener noreferrer" target="_blank">
                  <div class="footer-img-right-1" :style="{ backgroundImage: `url('${page.fields.footer_image}')` }"></div>
                </a>
              </aside>
            </div>
          </div>
        </div>
      </footer>
    </section>
  </div>
</template>

<script>
import axios from 'axios';
import Butter from 'buttercms';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import { Component, Vue } from 'vue-property-decorator';
import { email, required } from 'vuelidate/lib/validators';

export const butter = Butter('eb418357164849860c223876bb84811162582149');

@Component({
  components: {
    Loading,
  },
  data() {
    return {
      email: '',
      password: '',
      submitted: false,
      errorMsg: null,
      signInButtonDisabled: false,
      page: {
        fields: {},
      },
      isLoading: false,
    };
  },
  validations: {
    email: {
      required,
      email,
    },
    password: {
      required,
    },
  },
  methods: {
    getPage() {
      // this is a side effect of how we pass the auth header on every request butter tries to validate OUR token so we have to remove it
      delete axios.defaults.headers.common.Authorization;

      butter.page
        .retrieve('*', 'partners-home', { preview: `${process.env.VUE_APP_BUTTER_PREVIEW}` })
        .then((res) => {
          this.page = res.data.data;
        })
        .catch((res) => {
          // eslint-disable-next-line no-console
          console.log(res);
        });
      // add back our default header
      // axios.defaults.headers.common.Authorization = this.$store.state.auth.user.tokens.IdToken;
    },
    async signIn() {
      this.submitted = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      try {
        this.signInButtonDisabled = true;
        this.isLoading = true;

        await this.$store.dispatch('auth/authenticateUser', {
          username: this.email,
          password: this.password,
        });
        this.isLoading = false;
        this.signInButtonDisabled = false;

        if (!this.$store.state.auth.convertAccount) {
          axios.defaults.headers.common.Authorization = this.$store.state.auth.user.tokens.IdToken;
          // if (this.$store.state.auth.redirectUrl && this.$store.state.auth.redirectUrl !== '/') {
          //   const url = this.$store.state.auth.redirectUrl;
          //   this.$store.dispatch('auth/setRedirectUrl', null);
          //   this.$router.push(url);
          // } else if (this.$store.state.auth.user.role === 'admin' || this.$store.state.auth.user.role === 'editor') {
          //   this.$router.push('/admin');
          // } else {
          //   this.$router.push('/partners');
          // }
          // const url = this.$store.state.auth.redirectUrl;
          // console.log(`Redirect Url:${url}`);
          // CC 3-5-20 commented out the above because partners are being redirected to /admin for some reason
          if (this.$store.state.auth.user.role === 'limited_editor') {
            this.$router.push('/events');
          } else if (this.$store.state.auth.user.role === 'admin' || this.$store.state.auth.user.role === 'editor') {
            this.$router.push('/admin');
          } else {
            this.$router.push('/partners');
          }
        }
      } catch (err) {
        this.isLoading = false;
        this.errorMsg = 'This was not the correct username or password.  Please try again.';
        document.body.style.cursor = 'default';
        this.signInButtonDisabled = false;
      }
    },
  },
  created() {
    if (this.$store.state.auth.loggedIn) {
      if (this.$store.state.auth.user.role === 'limited_editor') {
        this.$router.push('/events');
      } else if (this.$store.state.auth.user.role === 'admin' || this.$store.state.auth.user.role === 'editor') {
        this.$router.push('/admin');
      } else {
        this.$router.push('/partners');
      }
    }
  },
  mounted() {
    this.getPage();
    this.$store.subscribe((mutation) => {
      switch (mutation.type) {
        case 'auth/convertAccount':
          this.$router.push({ name: 'signup', params: { email: this.email } });
          break;
        default:
          break;
      }
    });
    window.scrollTo(0, 0);
  },
  computed: {
    lowercaseEmail: {
      get() {
        return this.email;
      },
      set(value) {
        this.email = value.toLowerCase();
      }
    }
  }
})
class PartnersHome extends Vue {}
export default PartnersHome;
</script>

<style lang="scss" scoped>
.header-image {
  width: 100%;
  height: 300px;
  object-fit: cover;
}

.social-links-container {
  list-style-type: none;
  margin: 0;
  padding: 0;
  overflow: hidden;

  li {
    float: left;
  }

  .footer-social-img {
    background-repeat: no-repeat;
    background-size: 18px 18px;
    background-position: center;
  }

  .social-footer-link {
    float: left;
    position: relative;
    display: inline-block;
  }
}

.footer-social-img {
  width: 34px;
  height: 34px;
  margin: 3px 9px 3px 0px;
  padding: 8px;
  background-color: #1a3138;
  border-radius: 50px;
  -webkit-transition: background-color 350ms ease-in-out;
  -o-transition: background-color 350ms ease-in-out;
  transition: background-color 350ms ease-in-out;
}

.footer-social-img:hover {
  width: 34px;
  height: 34px;
  margin: 3px 9px 3px 0px;
  padding: 8px;
  background-color: #1a313866;
}

.social-footer-link:focus-visible {
  width: 42px;
  height: 42px;
  margin: -1px 5px -1px -4px;
  padding-top: 0px;
  border: dotted 3px #1a313866;
  outline: none;
  border-radius: 50px;
}

.social-footer-link:focus-visible .footer-social-img {
  background-color: #1a313866;
  margin-top: 1px;
  margin-left: 1px;
}

.privacy-policy {
  color: white;
}

footer .tourism-links a {
  display: inline-block;
}

img.atomsiconssocial {
  width: 18px;
  height: 18px;
  object-fit: contain;
}

.footer-block h6 {
  margin-bottom: 7px;
  width: 100%;
}

.tourism-links {
  margin-bottom: 5px;
}

.banner {
  background-size: cover;
  background-position: center;
  height: 300px;
}

.error-message {
  color: red;
  display: block;
}

.float-right {
  float: right;
}

.button {
  background-color: #4e97b2;
  border: none;
  border-radius: var(--rounded);
  color: white;
  display: inline-block;
  font-size: 1.1rem;
  font-weight: 500;
  min-height: 50px;
  padding: 15px 35px;
  position: relative;
  -webkit-transition: background-color 0.5s;
  transition: background-color 0.5s;
  text-decoration: underline;
}

.button:hover {
  background-color: #357e99;
}

.callout-img-right-1 {
  background-repeat: no-repeat;
  background-position: 100% 0%;
  background-size: 100%;
  margin-top: 50px;
  min-height: 625px;
}

.footer-control {
  height: 50px;
}

footer {
  margin-top: 40px;
  background: #4e97b2;
  bottom: 0;
  color: white !important;
  line-height: 12px;
  padding: 50px;
  font-size: 14px;
  position: relative;
  width: 100%;
}

.footer-img-right-1 {
  background-repeat: no-repeat;
  background-position: 100% 0%;
  background-size: 100%;
  height: 70px;
  float: right;
  margin-top: 35px;
  margin-right: 15px;
  width: 120px;
  clear: both;
}

.footer-img-social {
  background-repeat: no-repeat;
  background-position: 0% 100%;
  background-size: 100%;
  height: 50px;
  float: right;
  margin-top: 10px;
  margin-right: 8px;
  width: 50px;
}

.privacy-policy {
  color: white !important;
  text-decoration: underline;
}

.privacy-policy:hover {
  color: #cdebf3 !important;
}

.tourism-links {
  margin-bottom: 5px !important;
}

@media only screen and (max-width: 830px) {
  .callout-img-right-1 {
    background-size: 70%;
  }
}

@media only screen and (max-width: 700px) {
  .callout-img-right-1 {
    min-height: 485px;
  }
}

@media only screen and (max-width: 420px) {
  .callout-img-right-1 {
    min-height: 315px;
  }
}
</style>
